import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import "../assets/css/theme-table-header.css";
import { getSessionBranchId } from "../utils/sessionBranch";
import {
  createRefinery,
  updateRefinery,
  listSuppliers,
  showMetalGroup,
  showProductGroup,
  isErpApiSuccess,
} from "../erpClient";
import {
  searchProductGroupSelectOptions,
  mergeProductGroupOptions,
  fetchProductGroupOptionById,
  productGroupOptionDisplayName,
} from "../utils/productGroupOptions";
import {
  buildPoolByGroupIdFromOptions,
  validatePoolDeductions,
  numPool,
} from "../utils/productGroupPool";
import ProductGroupPoolBalance from "../components/ProductGroupPoolBalance";
import {
  createRefineryIdempotencyKey,
  isRefineryApiSuccess,
} from "../utils/refineryErpAdapter";
import {
  setupPortalContainers,
  teardownPortalContainers,
  getDropdownPortalTarget,
} from "../utils/portalContainers";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";

const COLUMNS = [
  { id: "no", label: "No.", width: "40px" },
  {
    id: "productGroup", label: (<>
      Product Group <span className="text-danger">*</span>
    </>), width: "200px"
  },
  { id: "hsn", label: "HSN", width: "80px" },
  {
    id: "gwt", label: (<>
      Gr.Wt <span className="text-danger">*</span>
    </>), width: "90px"
  },
  {
    id: "nwt", label: (<>
      Net.Wt <span className="text-danger">*</span>
    </>), width: "90px"
  },
  {
    id: "touch", label: (<>
      Touch <span className="text-danger">*</span>
    </>), width: "80px"
  },
  {
    id: "fine_wt", label: (<>
      Fine Wt <span className="text-danger">*</span>
    </>), width: "90px"
  },
  { id: "conv_item", label: "Conv Item", width: "90px" },
  {
    id: "total_wt", label: (<>
      Total Wt <span className="text-danger">*</span>
    </>), width: "90px"
  },
  { id: "pcs", label: "Pcs", width: "60px" },
];

const INPUT_STYLE = {
  fontSize: "0.85rem",
  padding: "0.4rem 0.5rem",
  height: "38px",
  backgroundColor: "transparent",
};

const getColIndex = (colId) => COLUMNS.findIndex((c) => c.id === colId);

const emptyRow = () => ({
  key: `row-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  product_group_id: null,
  product_group_name: "",
  item_category_name: "",
  hsn: "",
  gwt: "",
  nwt: "",
  touch: "",
  fine_wt: "",
  conv_item: "",
  total_wt: "",
  pcs: 1,
});

const round3 = (n) => Math.round(n * 1000) / 1000;

const calcFineWt = (nwt, touch) => {
  const n = parseFloat(nwt);
  const t = parseFloat(touch);
  if (!Number.isFinite(n) || !Number.isFinite(t) || n <= 0 || t <= 0) return "";
  return String(round3((n * t) / 100));
};

const parseNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

function RefineryForm({ mode = "create", initialData = null }) {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const isEdit = mode === "edit";

  const [date, setDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [remarks, setRemarks] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierMobile, setSupplierMobile] = useState("");
  const [supplierId, setSupplierId] = useState(null);
  const [items, setItems] = useState([emptyRow()]);
  const [productGroupOptions, setProductGroupOptions] = useState([]);
  const [hsnByMetalGroupId, setHsnByMetalGroupId] = useState({});
  const [saving, setSaving] = useState(false);
  const [docNumber, setDocNumber] = useState("");

  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyHighlightedIndex, setPartyHighlightedIndex] = useState(0);
  const partyNameInputRef = useRef(null);
  const partyDropdownRef = useRef(null);
  const partyItemsRef = useRef([]);
  const partySearchRequestRef = useRef(0);

  const mapSupplierToPartyOption = (karigar) => {
    const displayName =
      karigar.company_name ||
      karigar.companyName ||
      karigar.name ||
      karigar.fullName ||
      karigar.supplier_name ||
      karigar.short_code ||
      (karigar.id != null ? `Supplier #${karigar.id}` : "");
    const contactNumber =
      karigar.contact_number ||
      karigar.contactNumber ||
      karigar.mobile ||
      karigar.phone ||
      "";

    let label = displayName;
    if (contactNumber) {
      label = `${displayName} (${contactNumber})`;
    }

    return {
      value: karigar.id,
      label,
      name: displayName,
      mobile: contactNumber || "",
      id: karigar.id,
    };
  };

  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  const [pgDropdown, setPgDropdown] = useState({
    rowKey: null,
    options: [],
    highlight: 0,
    searchCalled: false,
    loading: false,
  });
  const [activePgRowKey, setActivePgRowKey] = useState(null);
  const pgInputRefs = useRef({});
  const pgDropdownRef = useRef(null);
  const pgDropdownItemsRef = useRef([]);
  const cellInputRefs = useRef({});
  const pgSearchTimerRef = useRef({});
  const lastSearchedPgPrefixRef = useRef({});

  const PG_DROPDOWN_VISIBLE_COUNT = 3;
  const PG_DROPDOWN_ITEM_HEIGHT = 35;
  const idempotencyKeyRef = useRef(null);
  const payloadFingerprintRef = useRef(null);

  const resolveHsnForMetalGroup = useCallback(
    async (metalGroupId) => {
      const id = Number(metalGroupId);
      if (!Number.isFinite(id) || id <= 0) return "";

      if (
        Object.prototype.hasOwnProperty.call(hsnByMetalGroupId, id) ||
        Object.prototype.hasOwnProperty.call(hsnByMetalGroupId, String(id))
      ) {
        return (
          hsnByMetalGroupId[id] ?? hsnByMetalGroupId[String(id)] ?? ""
        );
      }

      try {
        const mgRes = await showMetalGroup({ id });
        const mg = mgRes?.data?.metal_group ?? mgRes?.data;
        if (!isErpApiSuccess(mgRes) || !mg) return "";
        const hsn = mg.hsn_code ?? mg.hsn ?? "";
        setHsnByMetalGroupId((prev) => ({ ...prev, [id]: hsn }));
        return hsn;
      } catch (e) {
        console.error("Error loading metal group HSN:", e);
        return "";
      }
    },
    [hsnByMetalGroupId],
  );

  useEffect(() => {
    if (!initialData) return;
    setDocNumber(initialData.doc_number || "");
    setDate(initialData.doc_date || new Date().toISOString().split("T")[0]);
    setRemarks(initialData.remarks ?? "");
    setSupplierId(initialData.supplier_id ?? null);
    setSupplierName(initialData.supplier_name || "");
    setSupplierMobile(initialData.supplier_mobile || "");
    if (Array.isArray(initialData.items) && initialData.items.length > 0) {
      const mapped = initialData.items.map((item, idx) => ({
        key: item.id ? `line-${item.id}` : `row-${idx}`,
        product_group_id: item.product_group_id ?? null,
        product_group_name: item.product_group_name || "",
        item_category_name:
          item.item_category_name ||
          item.itemCategoryName ||
          item.product_group_name ||
          "",
        hsn: item.hsn ?? "",
        gwt: item.gwt != null && item.gwt !== "" ? String(item.gwt) : "",
        nwt: item.nwt != null && item.nwt !== "" ? String(item.nwt) : "",
        touch:
          item.touch != null && item.touch !== "" ? String(item.touch) : "",
        fine_wt:
          item.fine_wt != null && item.fine_wt !== ""
            ? String(item.fine_wt)
            : "",
        conv_item:
          item.conv_item != null && item.conv_item !== ""
            ? String(item.conv_item)
            : "",
        total_wt:
          item.total_wt != null && item.total_wt !== ""
            ? String(item.total_wt)
            : "",
        pcs: 1,
      }));
      setItems(mapped);
      const firstWithPg = mapped.find((r) => r.product_group_id);
      if (firstWithPg) setActivePgRowKey(firstWithPg.key);

      const uniquePgIds = [
        ...new Set(
          mapped
            .map((r) => Number(r.product_group_id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      ];
      if (uniquePgIds.length > 0) {
        Promise.all(uniquePgIds.map((id) => fetchProductGroupOptionById(id)))
          .then((resolved) => {
            const cacheItems = resolved.filter(Boolean);
            if (cacheItems.length > 0) {
              setProductGroupOptions((prev) =>
                mergeProductGroupOptions(prev, cacheItems),
              );
              const catById = new Map(
                cacheItems.map((opt) => [
                  Number(opt.value),
                  opt.item_category_name || "",
                ]),
              );
              setItems((prev) =>
                prev.map((row) => {
                  if (row.item_category_name) return row;
                  const cat = catById.get(Number(row.product_group_id));
                  return cat ? { ...row, item_category_name: cat } : row;
                }),
              );
            }
          })
          .catch((e) => console.error(e));
      }
    }
  }, [initialData]);

  const fetchPartyOptions = async (prefix) => {
    const requestId = ++partySearchRequestRef.current;
    if (!prefix || prefix.length < 2) {
      setPartyOptions([]);
      setShowPartyDropdown(false);
      return;
    }
    try {
      const response = await listSuppliers({ search: prefix, status: 1 });
      if (requestId !== partySearchRequestRef.current) return;
      if (!isErpApiSuccess(response)) {
        setPartyOptions([]);
        setShowPartyDropdown(false);
        return;
      }

      const list = extractListRowsFromResponse(response, ["suppliers"]);
      const options = list
        .filter((karigar) => {
          const status = String(karigar?.status ?? "").trim().toLowerCase();
          if (!status) return true;
          return ["1", "active", "enabled", "true"].includes(status);
        })
        .map(mapSupplierToPartyOption)
        .filter((option) => option.name);

      setPartyOptions(options);
      setShowPartyDropdown(options.length > 0);
      setPartyHighlightedIndex(0);
    } catch {
      if (requestId !== partySearchRequestRef.current) return;
      setPartyOptions([]);
      setShowPartyDropdown(false);
    }
  };

  const handlePartyNameChange = (e) => {
    const value = e.target.value;
    setSupplierName(value);
    setSupplierId(null);
    setSupplierMobile("");

    if (!value || value.length < 2) {
      setPartyOptions([]);
      setShowPartyDropdown(false);
      return;
    }

    fetchPartyOptions(value);
  };

  const handlePartySelect = (option) => {
    setSupplierName(option.name);
    setSupplierMobile(option.mobile || "");
    setSupplierId(option.id);
    setShowPartyDropdown(false);
    setPartyHighlightedIndex(0);
  };

  const handlePartyKeyDown = (e) => {
    if (!showPartyDropdown || partyOptions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setPartyHighlightedIndex((i) =>
        i < partyOptions.length - 1 ? i + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setPartyHighlightedIndex((i) =>
        i > 0 ? i - 1 : partyOptions.length - 1,
      );
    } else if (e.key === "Enter" && partyOptions[partyHighlightedIndex]) {
      e.preventDefault();
      handlePartySelect(partyOptions[partyHighlightedIndex]);
    } else if (e.key === "Escape") {
      setShowPartyDropdown(false);
    }
  };

  const searchProductGroups = useCallback(async (rowKey, prefix) => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setPgDropdown({
        rowKey: null,
        options: [],
        highlight: 0,
        searchCalled: false,
        loading: false,
      });
      return;
    }

    if (lastSearchedPgPrefixRef.current[rowKey] === trimmed) {
      setPgDropdown((d) => ({ ...d, searchCalled: true, loading: false }));
      return;
    }

    setPgDropdown((d) => ({
      ...d,
      rowKey,
      loading: true,
      searchCalled: false,
    }));
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      lastSearchedPgPrefixRef.current[rowKey] = trimmed;
      setProductGroupOptions((prev) => mergeProductGroupOptions(prev, opts));
      setPgDropdown({
        rowKey,
        options: opts,
        highlight: 0,
        searchCalled: true,
        loading: false,
      });
    } catch (e) {
      console.error(e);
      setPgDropdown({
        rowKey,
        options: [],
        highlight: 0,
        searchCalled: true,
        loading: false,
      });
    }
  }, []);

  const openProductGroupDropdown = (rowKey, searchText) => {
    const q = String(searchText || "").trim();
    if (q.length < 3) {
      setPgDropdown({
        rowKey: null,
        options: [],
        highlight: 0,
        searchCalled: false,
        loading: false,
      });
      lastSearchedPgPrefixRef.current[rowKey] = "";
      return;
    }

    if (pgSearchTimerRef.current[rowKey]) {
      clearTimeout(pgSearchTimerRef.current[rowKey]);
    }

    setPgDropdown((d) => ({
      ...d,
      rowKey,
      loading: true,
      searchCalled: false,
    }));

    pgSearchTimerRef.current[rowKey] = setTimeout(() => {
      searchProductGroups(rowKey, q);
    }, 300);
  };

  const selectProductGroup = async (rowKey, option, rowIndex) => {
    if (option) {
      setProductGroupOptions((prev) => mergeProductGroupOptions(prev, [option]));
    }

    setItems((prev) =>
      prev.map((row) =>
        row.key === rowKey
          ? {
            ...row,
            product_group_id: option?.value ?? null,
            product_group_name: option?.name ?? option?.label ?? "",
            item_category_name:
              option?.item_category_name ||
              option?.name ||
              option?.label ||
              "",
          }
          : row,
      ),
    );
    setPgDropdown({
      rowKey: null,
      options: [],
      highlight: 0,
      searchCalled: false,
      loading: false,
    });
    setActivePgRowKey(rowKey);
    setTimeout(() => {
      cellInputRefs.current[`${rowIndex}-hsn`]?.focus();
    }, 0);

    let metalGroupId = option?.metal_group_id;
    let categoryName = option?.item_category_name || "";
    if ((!metalGroupId || !categoryName) && option?.value) {
      try {
        const pgRes = await showProductGroup({ id: Number(option.value) });
        const pg = pgRes?.data?.product_group ?? pgRes?.data;
        if (isErpApiSuccess(pgRes) && pg) {
          if (pg.metal_group_id) metalGroupId = pg.metal_group_id;
          if (pg.item_category_name) categoryName = pg.item_category_name;
        }
      } catch (e) {
        console.error("Error fetching product group detail:", e);
      }
    }

    if (categoryName) {
      setItems((prev) =>
        prev.map((row) =>
          row.key === rowKey
            ? {
              ...row,
              item_category_name: categoryName,
            }
            : row,
        ),
      );
    }

    if (!metalGroupId) return;

    const hsn = await resolveHsnForMetalGroup(metalGroupId);
    if (!hsn) return;

    setItems((prev) =>
      prev.map((row) =>
        row.key === rowKey
          ? {
            ...row,
            hsn: row.hsn || hsn,
          }
          : row,
      ),
    );
  };

  const focusCell = (rowIndex, colId) => {
    cellInputRefs.current[`${rowIndex}-${colId}`]?.focus();
  };

  const addRowAtEnd = () => {
    setItems((prev) => [...prev, emptyRow()]);
  };

  const handleLastColumnFocus = (rowIndex) => {
    if (rowIndex !== items.length - 1) return;

    const row = items[rowIndex];
    const hasContent =
      row?.product_group_id || String(row?.product_group_name || "").trim();
    if (!hasContent) return;

    if (items.length === rowIndex + 1) {
      addRowAtEnd();
    }
    setTimeout(() => focusCell(rowIndex + 1, "productGroup"), 0);
  };

  const handleCellTab = (e, rowIndex, colId) => {
    if (e.key !== "Tab" || e.shiftKey) return;
    const colIndex = getColIndex(colId);
    if (colIndex === COLUMNS.length - 2 && rowIndex === items.length - 1) {
      // Tab from total_wt (last editable) on last row → add row
      e.preventDefault();
      addRowAtEnd();
      setTimeout(() => focusCell(rowIndex + 1, "productGroup"), 0);
    }
  };

  const updateItem = (key, field, value) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const next = { ...row, [field]: value };

        if (field === "gwt") {
          next.nwt = value;
        }

        if (field === "gwt" || field === "nwt" || field === "touch") {
          const nwtForCalc =
            field === "nwt" ? value : field === "gwt" ? value : next.nwt;
          const touchForCalc = field === "touch" ? value : next.touch;
          const autoFine = calcFineWt(nwtForCalc, touchForCalc);
          if (autoFine) {
            next.fine_wt = autoFine;
            next.total_wt = autoFine;
          }
        }

        if (field === "fine_wt") {
          next.total_wt = value;
        }

        if (field === "product_group_name") {
          next.product_group_id = null;
          next.item_category_name = "";
        }
        return next;
      }),
    );
  };

  const totals = useMemo(() => {
    let totalFineWt = 0;
    let totalRows = 0;
    items.forEach((row) => {
      if (row.product_group_id || row.product_group_name) totalRows += 1;
      const fw = parseNum(row.fine_wt);
      if (fw != null && fw > 0) totalFineWt += fw;
    });
    return { totalFineWt: round3(totalFineWt), totalRows };
  }, [items]);

  const productGroupById = useMemo(() => {
    const map = {};
    productGroupOptions.forEach((o) => {
      const id = Number(o.value);
      if (Number.isFinite(id) && id > 0) map[id] = o;
    });
    return map;
  }, [productGroupOptions]);

  const activePgBalance = useMemo(() => {
    if (
      pgDropdown.rowKey &&
      pgDropdown.options.length > 0 &&
      pgDropdown.options[pgDropdown.highlight]
    ) {
      const opt = pgDropdown.options[pgDropdown.highlight];
      return {
        pcs: numPool(opt.pool_pcs),
        nwt: numPool(opt.pool_nwt),
      };
    }

    if (!activePgRowKey) return null;
    const row = items.find((r) => r.key === activePgRowKey);
    if (!row?.product_group_id) return null;
    const opt = productGroupById[Number(row.product_group_id)];
    if (!opt) return null;
    return {
      pcs: numPool(opt.pool_pcs),
      nwt: numPool(opt.pool_nwt),
    };
  }, [activePgRowKey, items, productGroupById, pgDropdown]);

  const buildApiItems = () =>
    items
      .filter((row) => row.product_group_id)
      .map((row) => {
        const item = {
          product_group_id: Number(row.product_group_id),
          fine_wt: parseNum(row.fine_wt),
        };
        const categoryName = String(
          row.item_category_name || row.product_group_name || "",
        ).trim();
        if (categoryName) {
          item.item_category_name = categoryName;
        }
        if (row.hsn) item.hsn = row.hsn;
        const gwt = parseNum(row.gwt);
        const nwt = parseNum(row.nwt);
        const touch = parseNum(row.touch);
        const conv = parseNum(row.conv_item);
        const totalWt = parseNum(row.total_wt);
        if (gwt != null) item.gwt = gwt;
        if (nwt != null) item.nwt = nwt;
        if (touch != null) item.touch = round3(touch);
        if (conv != null) item.conv_item = round3(conv);
        if (totalWt != null) item.total_wt = totalWt;
        return item;
      });

  const handleSubmit = async () => {
    const sid = Number(supplierId);
    if (!Number.isFinite(sid) || sid <= 0) {
      toast.error("Please select a supplier");
      return;
    }
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    const apiItems = buildApiItems();
    if (apiItems.length < 1) {
      toast.error("Add at least one item row with product group");
      return;
    }
    for (let i = 0; i < apiItems.length; i++) {
      if (apiItems[i].fine_wt == null || apiItems[i].fine_wt <= 0) {
        toast.error(`Row ${i + 1}: fine weight must be greater than 0`);
        return;
      }
    }

    const poolByGroupId = buildPoolByGroupIdFromOptions(productGroupOptions);
    const poolCheck = validatePoolDeductions(apiItems, poolByGroupId, {
      mode: "refinery",
    });
    if (!poolCheck.valid) {
      toast.error(poolCheck.message);
      return;
    }

    const branchId = getSessionBranchId();
    const basePayload = {
      supplier_id: sid,
      date,
      remarks: remarks.trim() || undefined,
      items: apiItems,
      ...(branchId != null && !isEdit ? { branch_id: branchId } : null),
      ...(isEdit && initialData?.id ? { id: initialData.id } : null),
    };

    const fingerprint = JSON.stringify(basePayload);
    if (
      !idempotencyKeyRef.current ||
      payloadFingerprintRef.current !== fingerprint
    ) {
      idempotencyKeyRef.current = createRefineryIdempotencyKey();
      payloadFingerprintRef.current = fingerprint;
    }

    setSaving(true);
    try {
      const apiFn = isEdit ? updateRefinery : createRefinery;
      const response = await apiFn(
        { ...basePayload, idempotency_key: idempotencyKeyRef.current },
        { idempotencyKey: idempotencyKeyRef.current },
      );

      if (!isRefineryApiSuccess(response)) {
        toast.error(response?.message || "Failed to save refinery bill");
        return;
      }

      const savedDoc =
        response?.data?.doc_number ?? response?.doc_number ?? docNumber ?? "";
      toast.success(
        savedDoc ? `Refinery saved (${savedDoc})` : "Refinery bill saved",
      );
      idempotencyKeyRef.current = null;
      payloadFingerprintRef.current = null;

      navigate("/refinery");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save refinery bill",
      );
    } finally {
      setSaving(false);
    }
  };

  const renderPartyDropdown = () => {
    if (!showPartyDropdown || partyOptions.length === 0) return null;
    const inputEl = partyNameInputRef.current;
    if (!inputEl) return null;

    const portalTarget = getDropdownPortalTarget();
    if (!portalTarget) return null;

    const rect = inputEl.getBoundingClientRect();

    return ReactDOM.createPortal(
      <div
        ref={partyDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "fixed",
          top: `${rect.bottom}px`,
          left: `${rect.left}px`,
          width: `${Math.max(rect.width, 220)}px`,
          maxHeight: "180px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {partyOptions.map((option, idx) => (
          <div
            key={`${option.id}-${idx}`}
            ref={(el) => (partyItemsRef.current[idx] = el)}
            className="dropdown-item"
            style={{
              cursor: "pointer",
              padding: "0.25rem 1rem",
              fontSize: "0.85rem",
              backgroundColor:
                idx === partyHighlightedIndex
                  ? "var(--theme-primary)"
                  : "transparent",
              color: idx === partyHighlightedIndex ? "#fff" : "inherit",
            }}
            onMouseEnter={() => setPartyHighlightedIndex(idx)}
            onMouseDown={(e) => {
              e.preventDefault();
              handlePartySelect(option);
            }}
          >
            {option.label}
          </div>
        ))}
      </div>,
      portalTarget,
    );
  };

  const renderPgDropdown = () => {
    if (!pgDropdown.rowKey || (!pgDropdown.searchCalled && !pgDropdown.loading))
      return null;
    const inputEl = pgInputRefs.current[pgDropdown.rowKey];
    if (!inputEl) return null;
    const rect = inputEl.getBoundingClientRect();
    const portal = document.getElementById("dropdown-portal") || document.body;
    const hasOptions = pgDropdown.options.length > 0;
    const visibleItems = hasOptions
      ? Math.min(pgDropdown.options.length, PG_DROPDOWN_VISIBLE_COUNT)
      : 1;
    const dropdownHeight = visibleItems * PG_DROPDOWN_ITEM_HEIGHT;
    const dropdownWidth = Math.max(rect.width, 200);

    pgDropdownItemsRef.current = [];

    return ReactDOM.createPortal(
      <div
        ref={pgDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${rect.bottom + window.scrollY}px`,
          left: `${rect.left + window.scrollX}px`,
          width: `${dropdownWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY:
              hasOptions && pgDropdown.options.length > PG_DROPDOWN_VISIBLE_COUNT
                ? "auto"
                : "hidden",
            overflowX: "hidden",
          }}
        >
          {pgDropdown.loading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : hasOptions ? (
            pgDropdown.options.map((option, idx) => (
              <div
                key={option.value}
                ref={(el) => {
                  pgDropdownItemsRef.current[idx] = el;
                }}
                className="dropdown-item"
                title={option.name || option.label}
                style={{
                  cursor: "pointer",
                  padding: "0.4rem 0.75rem",
                  fontSize: "0.85rem",
                  minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
                  lineHeight: "1.2",
                  backgroundColor:
                    idx === pgDropdown.highlight
                      ? "var(--theme-primary)"
                      : "transparent",
                  color: idx === pgDropdown.highlight ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onMouseEnter={() =>
                  setPgDropdown((d) => ({ ...d, highlight: idx }))
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  const rowIndex = items.findIndex((r) => r.key === pgDropdown.rowKey);
                  selectProductGroup(pgDropdown.rowKey, option, rowIndex);
                }}
              >
                {productGroupOptionDisplayName(option)}
              </div>
            ))
          ) : pgDropdown.searchCalled ? (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
                minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
              }}
            >
              No matching items
            </div>
          ) : null}
        </div>
      </div>,
      portal,
    );
  };

  const renderCellInput = (row, colId, rowIndex) => {
    if (colId === "no") {
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ height: "38px" }}
        >
          <span style={{ fontSize: "0.85rem" }}>{rowIndex + 1}</span>
        </div>
      );
    }
    if (colId === "pcs") {
      return (
        <div
          tabIndex={0}
          className="d-flex align-items-center justify-content-center"
          style={{ height: "38px", outline: "none" }}
          onFocus={() => handleLastColumnFocus(rowIndex)}
        >
          <span style={{ fontSize: "0.85rem" }}>1</span>
        </div>
      );
    }
    if (colId === "hsn") {
      return (
        <input
          ref={(el) => {
            cellInputRefs.current[`${rowIndex}-hsn`] = el;
          }}
          type="text"
          value={row.hsn}
          onChange={(e) => updateItem(row.key, "hsn", e.target.value)}
          onKeyDown={(e) => handleCellTab(e, rowIndex, "hsn")}
          className={`form-control border-0 ${buttonClass}`}
          style={INPUT_STYLE}
          autoComplete="off"
        />
      );
    }
    if (colId === "productGroup") {
      return (
        <input
          ref={(el) => {
            pgInputRefs.current[row.key] = el;
            cellInputRefs.current[`${rowIndex}-productGroup`] = el;
          }}
          type="text"
          value={row.product_group_name}
          onChange={(e) => {
            const val = e.target.value;
            updateItem(row.key, "product_group_name", val);
            openProductGroupDropdown(row.key, val);
          }}
          onClick={() => {
            setActivePgRowKey(row.key);
            if (row.product_group_name && row.product_group_name.length >= 3) {
              openProductGroupDropdown(row.key, row.product_group_name);
            }
          }}
          onFocus={() => setActivePgRowKey(row.key)}
          onKeyDown={(e) => {
            if (!pgDropdown.rowKey || pgDropdown.rowKey !== row.key) return;
            if (e.key === "ArrowDown" && pgDropdown.options.length > 0) {
              e.preventDefault();
              setPgDropdown((d) => {
                const next =
                  d.highlight < d.options.length - 1 ? d.highlight + 1 : 0;
                setTimeout(() => {
                  pgDropdownItemsRef.current[next]?.scrollIntoView({
                    block: "nearest",
                  });
                }, 0);
                return { ...d, highlight: next };
              });
            } else if (e.key === "ArrowUp" && pgDropdown.options.length > 0) {
              e.preventDefault();
              setPgDropdown((d) => {
                const next =
                  d.highlight > 0 ? d.highlight - 1 : d.options.length - 1;
                setTimeout(() => {
                  pgDropdownItemsRef.current[next]?.scrollIntoView({
                    block: "nearest",
                  });
                }, 0);
                return { ...d, highlight: next };
              });
            } else if (
              e.key === "Enter" &&
              pgDropdown.options[pgDropdown.highlight]
            ) {
              e.preventDefault();
              selectProductGroup(
                row.key,
                pgDropdown.options[pgDropdown.highlight],
                rowIndex,
              );
            } else if (e.key === "Escape") {
              setPgDropdown({
                rowKey: null,
                options: [],
                highlight: 0,
                searchCalled: false,
                loading: false,
              });
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              setPgDropdown({
                rowKey: null,
                options: [],
                highlight: 0,
                searchCalled: false,
                loading: false,
              });
            }, 150);
          }}
          className={`form-control border-0 ${buttonClass}`}
          style={INPUT_STYLE}
          autoComplete="off"
        />
      );
    }

    const fieldMap = {
      gwt: "gwt",
      nwt: "nwt",
      touch: "touch",
      fine_wt: "fine_wt",
      conv_item: "conv_item",
      total_wt: "total_wt",
    };
    const field = fieldMap[colId];
    const step = colId === "touch" || colId === "conv_item" ? "0.01" : "0.001";

    return (
      <input
        ref={(el) => {
          cellInputRefs.current[`${rowIndex}-${colId}`] = el;
        }}
        type="number"
        step={step}
        value={row[field] ?? ""}
        onChange={(e) => updateItem(row.key, field, e.target.value)}
        onKeyDown={(e) => handleCellTab(e, rowIndex, colId)}
        className={`form-control border-0 ${buttonClass}`}
        style={INPUT_STYLE}
      />
    );
  };

  const pageTitle = isEdit
    ? `Edit Refinery${docNumber ? ` — ${docNumber}` : ""}`
    : "Add Refinery";

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">{pageTitle}</h4>
            <ProductGroupPoolBalance balance={activePgBalance} className="" />
          </div>
          <div className="card-body">
            <div className="row mb-1">
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "50px", fontSize: "0.85rem" }}
                  >
                    Date:
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    style={{ fontSize: "0.85rem" }}
                  />
                </div>
              </div>

              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center position-relative">
                  <label
                    className="form-label me-1 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "65px", fontSize: "0.85rem" }}
                  >
                    Supplier: <span className="text-danger">*</span>
                  </label>
                  <div className="position-relative flex-grow-1">
                    <input
                      ref={partyNameInputRef}
                      type="text"
                      className={`form-control form-control-sm ${buttonClass}`}
                      placeholder="Name"
                      value={supplierName}
                      onChange={handlePartyNameChange}
                      onFocus={() => {
                        if (partyOptions.length > 0) setShowPartyDropdown(true);
                      }}
                      onBlur={() =>
                        setTimeout(() => setShowPartyDropdown(false), 150)
                      }
                      onKeyDown={handlePartyKeyDown}
                      autoComplete="off"
                      style={{ fontSize: "0.85rem" }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-7 col-sm-12 mb-2">
                <input
                  type="text"
                  className={`form-control form-control-sm ${buttonClass}`}
                  placeholder="Mobile No"
                  value={supplierMobile}
                  readOnly
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
            </div>

            <div
              className={`table-responsive border ${buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
                } mt-3 ${cardClass}`}
              style={{
                maxHeight: "calc(38px * 8)",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <table
                className={`table table-bordered table-hover table-sm mb-0 theme-header-table ${tableClass}`}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: col.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, rowIndex) => (
                    <tr key={row.key} style={{ backgroundColor: "#ffffff" }}>
                      {COLUMNS.map((col) => (
                        <td
                          key={col.id}
                          className="p-0"
                          style={{ width: col.width }}
                        >
                          {renderCellInput(row, col.id, rowIndex)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    {COLUMNS.map((col) => {
                      if (col.id === "no") {
                        return (
                          <td key={col.id} className="p-0">
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{ fontSize: "0.85rem", fontWeight: 600 }}
                              >
                                Total
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "fine_wt") {
                        return (
                          <td key={col.id} className="p-0">
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{ fontSize: "0.85rem", fontWeight: 600 }}
                              >
                                {totals.totalFineWt.toFixed(3)}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "pcs") {
                        return (
                          <td key={col.id} className="p-0">
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{ fontSize: "0.85rem", fontWeight: 600 }}
                              >
                                {totals.totalRows}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      return <td key={col.id} className="p-0" />;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="row mb-3 mt-3">
              <div className="col-lg-6 col-md-6 col-sm-6">
                <div className="d-flex align-items-start mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "120px", paddingTop: "6px" }}
                  >
                    Remarks:
                  </label>
                  <textarea
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks"
                    maxLength="200"
                    rows="3"
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>
            </div>

            <div className="text-end mt-3">
              <button
                type="button"
                className={`btn btn-primary ${buttonClass}`}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? "Saving…" : isEdit ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {renderPartyDropdown()}
      {renderPgDropdown()}
    </div>
  );
}

export default RefineryForm;
